import React, { useEffect, Fragment, useState } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { DeleteBillingCharges, GetBillingCharges } from "../../../api/masters/billingChargesAPI";
import { CSVLink } from "react-csv";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";

const ListBillingCharges = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchBillingCharges = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetBillingCharges();
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          name: item.name,
          billingChargesGroup: item.billingChargesGroupId?.name || "", // if populated
          printSrNo: item.printSrNo,
          type: item.type,
          rateEditable: item.rateEditable,
          rate: item.rate,
          intlRate: item.intlRate,
          discountAllowed: item.discountAllowed,
          nature: item.nature,
          serviceTax: item.serviceTax,
          active: item.active,
          hsnCode: item.hsnCode,
          roomRates: item.roomRates,
          packageDetails: item.packageDetails
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchBillingCharges();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => navigate(`/masters/billing-charges/edit/${id}`);

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteBillingCharges(id);
          if (response.success) {
            showToast("success", response.message);
            setData(data.filter(item => item.id !== id));
            setFilteredData(filteredData.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message || "Something went wrong");
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "8%" },
    { name: "Name", selector: row => row.name, sortable: true, width: "10%" },
    { name: "Billing Group", selector: row => row.billingChargesGroup, width: "10%" },
    { name: "Print Sr No", selector: row => row.printSrNo, width: "10%" },
    { name: "Type", selector: row => row.type, width: "10%" },
    { name: "Rate Editable", selector: row => row.rateEditable, width: "10%" },
    { name: "Rate", selector: row => row.rate, width: "8%" },
    { name: "Intl Rate", selector: row => row.intlRate, width: "8%" },
    { name: "Discount Allowed", selector: row => row.discountAllowed, width: "15%" },
    { name: "Nature", selector: row => row.nature, width: "10%" },
    { name: "Service Tax", selector: row => row.serviceTax, width: "8%" },
    { name: "HSN Code", selector: row => row.hsnCode, width: "8%" },
    { name: "Active", selector: row => row.active, width: "5%" },
    {
      name: "Room Rates",
      cell: (row) => (
        <div>
          {row.roomRates.length > 0 ? (
            row.roomRates.map((roomRate, i) => (
              <div key={i}>
                <b>{roomRate.roomCategory}</b> — {roomRate.rates}
              </div>
            ))
          ) : (
            <span>-</span>
          )}
        </div>
      ),
      grow: 2,
      width: "20%"
    },
    {
      name: "Room Rates",
      cell: (row) => (
        <div>
          {row.packageDetails.length > 0 ? (
            row.packageDetails.map((packageDetail, i) => (
              <div key={i}>
                <b>{packageDetail.chargesName}</b> — {packageDetail.rates}
              </div>
            ))
          ) : (
            <span>-</span>
          )}
        </div>
      ),
      grow: 2,
      width: "20%"
    },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button className="btn btn-primary me-2" onClick={() => handleEditClick(row.id)} id={`edit-${row.id}`}>
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>Edit</UncontrolledTooltip>

          <button className="btn btn-danger" onClick={() => handleDeleteClick(row.id)} id={`delete-${row.id}`}>
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>Delete</UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "12%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
    { label: "Name", key: "name" },
    { label: "Billing Group", key: "billingChargesGroup" },
    { label: "Print Sr No", key: "printSrNo" },
    { label: "Type", key: "type" },
    { label: "Rate Editable", key: "rateEditable" },
    { label: "Rate", key: "rate" },
    { label: "Intl Rate", key: "intlRate" },
    { label: "Discount Allowed", key: "discountAllowed" },
    { label: "Nature", key: "nature" },
    { label: "Service Tax", key: "serviceTax" },
    { label: "HSN Code", key: "hsnCode" },
    { label: "Active", key: "active" },
    { label: "Room Rates", key: "roomRates" },
    { label: "Package Details", key: "packageDetails" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="billing-charges.csv">
      <button className="btn btn-primary" id="export" style={{ marginTop: "5px" }}>
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">Export to CSV</UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Billing Charges" title="List Billing Charges" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <h5>Billing Charges</h5>
                  <Link to={`/masters/billing-charges/add`}>
                    <button className="btn btn-primary" id="create">Create Billing Charge</button>
                    <UncontrolledTooltip placement="top" target="create">Create Billing Charge</UncontrolledTooltip>
                  </Link>
                </div>
                <div className="row">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search billing charges..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{ marginTop: "5px" }}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListBillingCharges;
